import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Box, Typography, Paper, Button, TextField, Stack, Chip, Checkbox, MenuItem, CircularProgress, Alert,
  Table, TableBody, TableCell, TableHead, TableRow, TableContainer, Dialog, DialogContent, DialogActions, Divider,
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import SaveIcon from '@mui/icons-material/Save';
import VisibilityIcon from '@mui/icons-material/Visibility';
import MovieIcon from '@mui/icons-material/Movie';
import toast from 'react-hot-toast';
import { api, baseApi } from '../../api/api';

// TLJ 메뉴보드 — 지역 매니저가 점주 미팅에서 매장 메뉴·가격·화면을 저장하면 화면별 이미지 자동 생성 (12.01-D014)
// 저장 → 현장 다운로드 · 다인텍 디자이너/IT 메일. TV 반영은 디자이너.

const SCREEN_LABEL = { E: '에스프레소', T: '티·커피', S: '시그니처·콜드', M: '비디오' };
const GROUPS = [
  { key: 'E', label: 'E · 에스프레소', test: (i) => i.screen === 'E' && i.category !== 'addon' },
  { key: 'T', label: 'T · 브루드 커피 · 티', test: (i) => i.screen === 'T' },
  { key: 'S', label: 'S · 초콜릿 · 콜드', test: (i) => i.screen === 'S' && i.category !== 'seasonal' },
  { key: 'seasonal', label: '시즈널', test: (i) => i.category === 'seasonal' },
  { key: 'addon', label: '추가 옵션', test: (i) => i.category === 'addon' },
];
const dollars = (c) => (c == null || c === '' ? '' : (c / 100).toFixed(2));
const cents = (v) => (v === '' || v == null ? null : Math.round(parseFloat(String(v).replace('$', '')) * 100));
const fileUrl = (storeId, v, f, dl) => `${baseApi}/menu-boards/${storeId}/versions/${v}/files/${f}${dl ? '?download=1' : ''}`;

const EMPTY_STORE = { name: '', tljNo: '', zipcode: '', town: '', state: '', address1: '', ownerName: '', ownerEmail: '', ownerPhone: '', pricingTier: 'tier1', screenCount: 3 };

/** 신규 매장 등록 — 지역 매니저가 점주 미팅에서 바로 (온보딩 단계로 시작) */
function NewStoreDialog({ open, onClose, onCreated }) {
  const [f, setF] = useState(EMPTY_STORE);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF((x) => ({ ...x, [k]: e.target.value }));
  useEffect(() => {
    const zip = f.zipcode.replace(/\D/g, '');
    if (zip.length !== 5) return;
    api.get(`/zipcode/${zip}`).then((r) => setF((x) => ({ ...x, town: x.town || r.data.city || '', state: x.state || r.data.stateAbbr || '',
      name: x.name || `${r.data.stateAbbr || ''}_${(r.data.city || '').replace(/\s+/g, '')}` }))).catch(() => {});
  }, [f.zipcode]);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post('/menu-boards/stores', { ...f, screenCount: Number(f.screenCount) });
      toast.success(`${r.data.store.name} 등록 — TV ${r.data.store.screenCount}대`);
      setF(EMPTY_STORE); onCreated(r.data.store);
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent>
        <Typography fontWeight={700} mb={2}>신규 매장 등록</Typography>
        <Stack spacing={1.5}>
          <TextField size="small" label="우편번호 (ZIP)" value={f.zipcode} onChange={set('zipcode')} inputProps={{ inputMode: 'numeric' }} />
          <TextField size="small" label="매장 이름 (예: NJ_Paramus)" value={f.name} onChange={set('name')} required />
          <Stack direction="row" spacing={1}>
            <TextField size="small" label="도시" value={f.town} onChange={set('town')} fullWidth />
            <TextField size="small" label="주" value={f.state} onChange={set('state')} sx={{ width: 90 }} />
          </Stack>
          <TextField size="small" label="주소" value={f.address1} onChange={set('address1')} />
          <TextField size="small" label="TLJ 매장 번호" value={f.tljNo} onChange={set('tljNo')} />
          <Stack direction="row" spacing={1}>
            <TextField select size="small" label="TV(스크린) 수" value={f.screenCount} onChange={set('screenCount')} fullWidth helperText="나중에 바꿀 수 있습니다">
              {[1, 2, 3, 4, 5, 6].map((n) => <MenuItem key={n} value={n}>{n}대</MenuItem>)}
            </TextField>
            <TextField select size="small" label="가격 단계" value={f.pricingTier} onChange={set('pricingTier')} fullWidth>
              {['tier1', 'tier2', 'tier3'].map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
            </TextField>
          </Stack>
          <Divider />
          <TextField size="small" label="점주 이름" value={f.ownerName} onChange={set('ownerName')} />
          <Stack direction="row" spacing={1}>
            <TextField size="small" label="점주 이메일" value={f.ownerEmail} onChange={set('ownerEmail')} fullWidth />
            <TextField size="small" label="점주 전화" value={f.ownerPhone} onChange={set('ownerPhone')} fullWidth />
          </Stack>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>취소</Button>
        <Button variant="contained" disabled={busy || !f.name.trim()} onClick={submit}>등록하고 메뉴보드 만들기</Button>
      </DialogActions>
    </Dialog>
  );
}

const SIZE_KO = { hot_s: 'HOT 12oz', hot_m: 'HOT 16oz', ice_s: 'ICED 16oz', ice_m: 'ICED 20oz' };
const usd = (c) => (c == null ? '—' : `$${(c / 100).toFixed(2)}`);

/** 일러스트 가격 가져오기 (12.01-D016) — tools/menu-ai-reader 가 만든 JSON → 미리보기 → 적용 */
function AiImportDialog({ open, storeId, onClose, onApplied }) {
  const [payload, setPayload] = useState(null);
  const [pv, setPv] = useState(null);
  const [excluded, setExcluded] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  useEffect(() => { if (!open) { setPayload(null); setPv(null); setExcluded([]); setShowInfo(false); } }, [open]);
  const pick = async (file) => {
    if (!file) return;
    try {
      const json = JSON.parse(await file.text());
      setBusy(true);
      const r = await api.post(`/menu-boards/${storeId}/ai-prices/preview`, { payload: json });
      setPayload(json); setPv(r.data); setExcluded([]);
    } catch (e) { toast.error(e.response?.data?.message || `JSON 을 읽지 못했습니다: ${e.message}`); } finally { setBusy(false); }
  };
  const apply = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/menu-boards/${storeId}/ai-prices/apply`, { payload, excluded });
      toast.success(`매장 가격표에 반영 — 메뉴 ${r.data.applied.menus}개, 바뀐 메뉴 ${r.data.applied.changed}개`);
      onApplied();
    } catch (e) { toast.error(e.response?.data?.message || e.message); } finally { setBusy(false); }
  };
  const open_ = pv ? pv.unmatched.filter((u) => !excluded.includes(u.text)) : [];
  const lintShown = pv ? pv.lint.filter((l) => showInfo || l.level !== 'info') : [];
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogContent>
        <Typography variant="h6" fontWeight={700}>일러스트 가격 가져오기</Typography>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          사무실 Mac 에서 <code>python3 tools/menu-ai-reader/read_ai_prices.py &lt;Drive 매장 폴더&gt;</code> 로 만든 <code>.prices.json</code> 을 올리세요. 최신 .ai 파일의 표 가격을 읽은 것입니다.
        </Typography>
        <Button variant="outlined" component="label" disabled={busy}>
          JSON 파일 선택<input hidden type="file" accept=".json,application/json" onChange={(e) => pick(e.target.files[0])} />
        </Button>
        {busy && <CircularProgress size={20} sx={{ ml: 2 }} />}
        {pv && (
          <Box mt={2}>
            <Typography fontWeight={700}>{pv.file.folder} / {pv.file.file}</Typography>
            <Typography variant="body2" color="text.secondary">파일 날짜 {pv.file.fileDate || '?'} · 화면 {pv.file.layout} · 읽은 메뉴 {pv.items}개 · 바뀌는 칸 {pv.changes.length}개</Typography>
            {pv.folderMismatch && <Alert severity="error" sx={{ mt: 1 }}>{pv.folderMismatch}</Alert>}
            {!pv.store.driveFolder && !pv.folderMismatch && <Alert severity="info" sx={{ mt: 1 }}>적용하면 이 매장이 Drive 폴더 "{pv.file.folder}" 와 연결됩니다.</Alert>}

            {pv.unmatched.length > 0 && (
              <Alert severity={open_.length ? 'warning' : 'success'} sx={{ mt: 1 }}>
                <Typography variant="body2" fontWeight={700}>표준 메뉴에 없는 줄 {pv.unmatched.length}개 — 모두 확인해야 적용됩니다 (매장 메뉴로 안 씀 = 제외)</Typography>
                {pv.unmatched.map((u) => (
                  <Stack key={u.text} direction="row" alignItems="center">
                    <Checkbox size="small" checked={excluded.includes(u.text)} onChange={(e) => setExcluded((x) => (e.target.checked ? [...x, u.text] : x.filter((t) => t !== u.text)))} />
                    <Typography variant="body2">{u.text} <Typography component="span" variant="caption" color="text.secondary">({u.values.join(' ')})</Typography> — 제외</Typography>
                  </Stack>
                ))}
                <Typography variant="caption">계속 쓰는 메뉴면 제외하지 말고 표준 메뉴에 추가 요청 (TLJ 확인)</Typography>
              </Alert>
            )}

            <TableContainer sx={{ maxHeight: 320, mt: 1 }}>
              <Table size="small" stickyHeader>
                <TableHead><TableRow><TableCell>메뉴</TableCell><TableCell>사이즈</TableCell><TableCell align="right">지금</TableCell><TableCell align="right">일러스트</TableCell></TableRow></TableHead>
                <TableBody>
                  {pv.changes.map((c) => (
                    <TableRow key={`${c.code}.${c.size}`}>
                      <TableCell>{c.name}</TableCell><TableCell>{SIZE_KO[c.size] || c.size}</TableCell>
                      <TableCell align="right" sx={{ color: 'text.secondary' }}>{usd(c.before)}</TableCell><TableCell align="right" sx={{ fontWeight: 700 }}>{usd(c.after)}</TableCell>
                    </TableRow>
                  ))}
                  {!pv.changes.length && <TableRow><TableCell colSpan={4}>매장 가격표와 같습니다</TableCell></TableRow>}
                </TableBody>
              </Table>
            </TableContainer>
            {pv.notOnFile.length > 0 && <Typography variant="caption" color="text.secondary">매장 가격표에만 있는 메뉴 {pv.notOnFile.length}개는 그대로 둡니다.</Typography>}

            <Divider sx={{ my: 1 }} />
            <Typography variant="body2" fontWeight={700}>템플릿 규격 검사 — 오류 {pv.lintCount.error} · 경고 {pv.lintCount.warn} · 참고 {pv.lintCount.info} <Typography component="span" variant="caption">(적용은 막지 않음, 디자이너 수정 목록)</Typography></Typography>
            {lintShown.slice(0, 40).map((l, i) => <Typography key={i} variant="caption" display="block" color={l.level === 'error' ? 'error' : l.level === 'warn' ? 'warning.main' : 'text.secondary'}>{['E', 'T', 'S'][l.page] || l.page + 1}화면 · {l.msg}</Typography>)}
            {pv.lintCount.info > 0 && <Button size="small" onClick={() => setShowInfo((v) => !v)}>{showInfo ? '참고 숨기기' : `참고(칼로리 차이 등) ${pv.lintCount.info}개 보기`}</Button>}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
        <Button variant="contained" disabled={!pv || busy || !!pv.folderMismatch || open_.length > 0} onClick={apply}>매장 가격표에 적용</Button>
      </DialogActions>
    </Dialog>
  );
}

/** 프로모 썸네일 (인증 필요 → blob) */
function PromoThumb({ id, h = 90 }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let u = null, live = true;
    if (id) api.get(`/menu-promos/${id}/image`, { responseType: 'blob' }).then((r) => { u = URL.createObjectURL(r.data); if (live) setUrl(u); }).catch(() => {});
    return () => { live = false; if (u) URL.revokeObjectURL(u); };
  }, [id]);
  return url ? <img src={url} alt="" style={{ height: h, borderRadius: 4, border: '1px solid #ddd' }} /> : <Box sx={{ height: h, width: h * 0.9, bgcolor: 'grey.100', borderRadius: 1 }} />;
}

/** 프로모 올리기 (다인텍) — extract_promo.py 의 promo.json + promo.jpg */
function PromoUploadDialog({ open, onClose, onDone }) {
  const [meta, setMeta] = useState(null);
  const [image, setImage] = useState(null);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (!open) { setMeta(null); setImage(null); setName(''); } }, [open]);
  const readJson = async (f) => { if (!f) return; try { const m = JSON.parse(await f.text()); setMeta(m); setName(m.name || ''); } catch (e) { toast.error(`promo.json 을 읽지 못했습니다: ${e.message}`); } };
  const readImg = (f) => { if (!f) return; const r = new FileReader(); r.onload = () => setImage(r.result); r.readAsDataURL(f); };
  const send = async () => {
    setBusy(true);
    try { await api.post('/menu-promos', { meta: { ...meta, name }, image }); toast.success('프로모를 올렸습니다'); onDone(); } catch (e) { toast.error(e.response?.data?.message || e.message); }
    setBusy(false);
  };
  const linked = meta ? (meta.slots || []).filter((x) => x.code).length : 0;
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogContent>
        <Typography variant="h6" fontWeight={700}>프로모 올리기</Typography>
        <Typography variant="body2" color="text.secondary" mb={1.5}>
          <code>python3 tools/menu-ai-reader/extract_promo.py &lt;매장 폴더&gt; --page 0 --name "이름" --out 폴더</code> 로 만든 promo.json 과 promo.jpg 를 고르세요. 가격 숫자는 지워져 있고, 표준 메뉴에 연결된 칸은 매장 가격으로 채워집니다.
        </Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="outlined" component="label">promo.json<input hidden type="file" accept=".json" onChange={(e) => readJson(e.target.files[0])} /></Button>
          <Button variant="outlined" component="label">그림(JPG/PNG)<input hidden type="file" accept="image/jpeg,image/png" onChange={(e) => readImg(e.target.files[0])} /></Button>
        </Stack>
        {meta && (
          <Box mt={2}>
            <TextField size="small" fullWidth label="이름" value={name} onChange={(e) => setName(e.target.value)} />
            <Typography variant="body2" mt={1}>가격·칼로리 칸 {(meta.slots || []).length}개 · 매장 가격 연결 {linked}개</Typography>
            {(meta.cards || []).map((c) => <Typography key={c.name} variant="caption" display="block" color={c.code ? 'success.main' : 'text.secondary'}>{c.name} — {c.code ? `표준 메뉴 ${c.code}` : '연결 없음 (원래 숫자 그대로)'}</Typography>)}
          </Box>
        )}
        {image && <img src={image} alt="" style={{ height: 160, marginTop: 8, border: '1px solid #ddd' }} />}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>닫기</Button>
        <Button variant="contained" disabled={!meta || !image || !name || busy} onClick={send}>올리기</Button>
      </DialogActions>
    </Dialog>
  );
}

function StorePicker() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [q, setQ] = useState('');
  const [adding, setAdding] = useState(false);
  useEffect(() => { api.get('/menu-boards').then((r) => setData(r.data)).catch((e) => toast.error(e.response?.data?.message || e.message)); }, []);
  if (!data) return <Box p={4} textAlign="center"><CircularProgress /></Box>;
  const list = data.stores.filter((s) => `${s.name} ${s.tljNo || ''}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <Box p={{ xs: 1.5, md: 3 }}>
      <Typography variant="h5" fontWeight={700}>메뉴보드</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>매장을 고르고 메뉴·가격·화면을 저장하면 메뉴보드 이미지가 바로 만들어집니다.</Typography>
      <Stack direction="row" spacing={1} mb={2} alignItems="center">
        <TextField size="small" fullWidth placeholder="매장 검색" value={q} onChange={(e) => setQ(e.target.value)} sx={{ maxWidth: 420 }} />
        {data.canSave && <Button variant="contained" onClick={() => setAdding(true)} sx={{ whiteSpace: 'nowrap' }}>+ 신규 매장</Button>}
      </Stack>
      <NewStoreDialog open={adding} onClose={() => setAdding(false)} onCreated={(st) => { setAdding(false); nav(`/menu-board/${st._id}`); }} />
      <Stack spacing={1}>
        {list.map((s) => (
          <Paper key={s._id} variant="outlined" sx={{ p: 1.5, cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }} onClick={() => nav(`/menu-board/${s._id}`)}>
            <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1}>
              <Box><Typography fontWeight={600}>{s.name}</Typography><Typography variant="caption" color="text.secondary">{s.tljNo || ''} {s.tljRegion || ''} · {s.pricingTier || 'tier1'}</Typography></Box>
              {s.screenCount ? <Chip size="small" variant="outlined" label={`TV ${s.screenCount}`} sx={{ mr: 0.5 }} /> : null}
              {s.last ? <Chip size="small" label={`v${s.last.version} · ${s.last.combo.join(' ')} · ${new Date(s.last.at).toLocaleDateString()}`} /> : <Chip size="small" variant="outlined" label="아직 없음" />}
            </Stack>
          </Paper>
        ))}
        {!list.length && <Typography color="text.secondary">볼 수 있는 매장이 없습니다.</Typography>}
      </Stack>
    </Box>
  );
}

function Editor({ storeId }) {
  const nav = useNavigate();
  const [sp] = useSearchParams();
  const [data, setData] = useState(null);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(3);
  const [design, setDesign] = useState('');
  const [combo, setCombo] = useState(null);
  const [videos, setVideos] = useState([]);
  const [videoChoice, setVideoChoice] = useState({});
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [preview, setPreview] = useState(null);
  const [importing, setImporting] = useState(false);
  const [fitInfo, setFitInfo] = useState(null);      // 표마다 들어간 수/최대
  const [full, setFull] = useState(null);            // { code, o: overflow 항목, off: [끌 메뉴] }
  const [promos, setPromos] = useState([]);          // 오른쪽 프로모 패널 목록
  const [promoSel, setPromoSel] = useState({});      // { E: promoId }
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    const [ed, hv, vids] = await Promise.all([
      api.get(`/menu-boards/${storeId}`), api.get(`/menu-boards/${storeId}/versions`), api.get(`/menu-boards/${storeId}/videos`).catch(() => ({ data: { videos: [] } })),
    ]);
    setData(ed.data); setItems(ed.data.items); setHistory(hv.data.versions); setVideos(vids.data.videos);
    setCount(ed.data.screenCount || 3);
    const cur = ed.data.current;
    const keep = cur && cur.combo.length === ed.data.screenCount ? cur : null;
    setDesign(keep?.design || ''); setCombo(keep?.combo || null);
    setPromoSel(Object.fromEntries((keep?.screens || []).filter((x) => x.promo && x.promo.id).map((x) => [x.code, x.promo.id])));
    api.get('/menu-promos').then((r) => setPromos(r.data.promos)).catch(() => setPromos([]));
    const want = Number(sp.get('v'));
    if (want) { const v = await api.get(`/menu-boards/${storeId}/versions/${want}`).catch(() => null); if (v) setResult(v.data.version); }
  }, [storeId, sp]);
  useEffect(() => { load().catch((e) => toast.error(e.response?.data?.message || e.message)); }, [load]);

  const designLabel = useMemo(() => Object.fromEntries((data?.designs || []).map((d) => [d.id, d.label])), [data]);
  const chosen = combo && design ? { combo, design } : null;
  const mScreens = combo ? combo.filter((c) => c === 'M').length : 0;
  const pickCombo = (o) => { if (!o.designs.length) return; setCombo(o.combo); setDesign(o.designs.includes(design) ? design : o.designs[0]); };

  // TV 수 변경 = 매장 정보 변경 (자리표시 스크린도 맞춤)
  const changeCount = async (n) => {
    if (n === count) return;
    if (!data.canChangeCount) return toast.error('TV 수 변경 권한이 없습니다');
    if (!window.confirm(`이 매장 TV(스크린) 수를 ${count}대 → ${n}대로 바꿀까요?`)) return;
    try {
      const r = await api.put(`/menu-boards/${storeId}/screen-count`, { screenCount: n });
      setCount(r.data.screenCount); setData((d) => ({ ...d, combos: r.data.combos, screenCount: r.data.screenCount }));
      setCombo(null); toast.success(`TV ${r.data.screenCount}대로 저장`);
    } catch (e) { toast.error(e.response?.data?.message || e.message); }
  };

  const setItem = (code, patch) => setItems((xs) => xs.map((i) => (i.code === code ? { ...i, ...patch } : i)));
  const setPrice = (code, size, v) => setItems((xs) => xs.map((i) => (i.code === code ? { ...i, prices: { ...i.prices, [size]: cents(v) } } : i)));

  const promoBody = () => Object.fromEntries(Object.entries(promoSel).filter(([k, v]) => v && combo && combo.includes(k)));
  const body = () => ({
    design, combo, tier: data.store.tier, note, promos: promoBody(),
    items: items.map((i) => ({ code: i.code, enabled: i.enabled, prices: Object.fromEntries(Object.entries(i.prices).filter(([, v]) => v != null)) })),
    videos: Array.from({ length: mScreens }, (_, k) => {
      const c = videoChoice[k];
      if (!c || c === 'default') return { source: 'default' };
      const v = videos.find((x) => String(x.contentId) === c);
      return v ? { source: v.photovid ? 'photovid' : 'content', contentId: v.contentId, name: v.name, url: v.url } : { source: 'default' };
    }),
  });

  // 칸 검사: 조합·메뉴가 바뀌면 표마다 몇 개/최대 몇 개인지
  const fitBody = (xs) => ({ design, combo, promos: promoBody(), items: xs.map((i) => ({ code: i.code, enabled: i.enabled, prices: Object.fromEntries(Object.entries(i.prices).filter(([, v]) => v != null)) })) });
  useEffect(() => {
    if (!design || !combo) { setFitInfo(null); return undefined; }
    const t = setTimeout(() => { api.post(`/menu-boards/${storeId}/fit`, fitBody(items)).then((r) => setFitInfo(r.data)).catch(() => setFitInfo(null)); }, 400);
    return () => clearTimeout(t);
  }, [design, combo, items, promoSel, storeId]); // eslint-disable-line react-hooks/exhaustive-deps

  // 메뉴 켜기: 그 표가 가득 차 있으면 먼저 끌 메뉴를 고르게 함
  const toggleItem = async (code, on) => {
    if (!on || !design || !combo) return setItem(code, { enabled: on });
    const next = items.map((i) => (i.code === code ? { ...i, enabled: true } : i));
    try {
      const r = await api.post(`/menu-boards/${storeId}/fit`, fitBody(next));
      const o = r.data.overflow.find((x) => x.code === code);
      if (o) return setFull({ code, o, off: [] });
    } catch (_) { /* 검사 실패면 저장 때 다시 검사 */ }
    setItem(code, { enabled: true });
  };
  const nameOf = (c) => items.find((i) => i.code === c)?.name || c;
  const confirmSwap = () => {
    const { code, off } = full;
    setItems((xs) => xs.map((i) => (i.code === code ? { ...i, enabled: true } : off.includes(i.code) ? { ...i, enabled: false } : i)));
    setFull(null);
  };

  const doPreview = async (screen) => {
    try {
      const r = await api.post(`/menu-boards/${storeId}/preview`, { ...body(), screen }, { responseType: 'blob' });
      const warnings = JSON.parse(decodeURIComponent(r.headers['x-warnings'] || '%5B%5D'));
      setPreview({ screen, url: URL.createObjectURL(r.data), warnings });
    } catch (e) {
      const msg = e.response?.data instanceof Blob ? JSON.parse(await e.response.data.text()).message : e.message;
      toast.error(msg);
    }
  };

  const save = async () => {
    if (!chosen) return toast.error('화면 조합을 고르세요');
    setSaving(true);
    try {
      const r = await api.post(`/menu-boards/${storeId}`, body());
      setResult(r.data.version);
      toast.success(`v${r.data.version.version} 저장 — 이미지 ${r.data.version.screens.filter((s) => s.file).length}장`);
      const hv = await api.get(`/menu-boards/${storeId}/versions`); setHistory(hv.data.versions);
    } catch (e) {
      const o = e.response?.data?.overflow?.[0];
      if (o) setFull({ code: o.code, o, off: [], fromSave: true, enabledNow: true });
      toast.error(e.response?.data?.message || e.message);
    }
    setSaving(false);
  };

  if (!data) return <Box p={4} textAlign="center"><CircularProgress /></Box>;
  const S = data.sizes, L = data.sizeLabels;

  return (
    <Box p={{ xs: 1, md: 3 }} pb={10}>
      <Button size="small" onClick={() => nav('/menu-board')}>← 매장 목록</Button>
      <Typography variant="h5" fontWeight={700}>{data.store.name} <Typography component="span" color="text.secondary">{data.store.tljNo || ''} · {data.store.tier}</Typography></Typography>
      {data.current && <Typography variant="body2" color="text.secondary">마지막 저장 v{data.current.version} · {new Date(data.current.savedAt).toLocaleString()} · {data.current.savedBy?.name || ''}</Typography>}
      <Stack direction="row" spacing={1} alignItems="center" mt={0.5} flexWrap="wrap" useFlexGap>
        <Chip size="small" label={{ illustrator: '시작값: 일러스트 가격', version: '시작값: 마지막 저장', store: '시작값: 매장 가격표', tier: '시작값: Tier 기본 가격' }[data.priceSource] || data.priceSource} color={data.priceSource === 'illustrator' ? 'success' : data.priceSource === 'tier' ? 'warning' : 'default'} />
        {data.store.aiPrices && <Typography variant="caption" color="text.secondary">일러스트 {data.store.aiPrices.folder}/{data.store.aiPrices.file} · {new Date(data.store.aiPrices.importedAt).toLocaleString()} 가져옴</Typography>}
        {data.canImport && <Button size="small" variant="outlined" onClick={() => setImporting(true)}>일러스트 가격 가져오기</Button>}
      </Stack>
      <AiImportDialog open={importing} storeId={storeId} onClose={() => setImporting(false)} onApplied={() => { setImporting(false); load(); }} />

      {/* 1. 화면 */}
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography fontWeight={700} mb={1}>1. TV 수 · 화면 조합</Typography>
        <Stack direction="row" spacing={1} alignItems="center" mb={1.5}>
          <TextField select size="small" label="TV(스크린) 수" value={count} onChange={(e) => changeCount(Number(e.target.value))} sx={{ width: 140 }} disabled={!data.canChangeCount}>
            {[1, 2, 3, 4, 5, 6].map((n) => <MenuItem key={n} value={n}>{n}대</MenuItem>)}
          </TextField>
          <Typography variant="caption" color="text.secondary">매장 TV 수는 여기서 바꿀 수 있습니다. E·T·S 를 나누거나 합치고, 남는 TV 는 M(비디오).</Typography>
        </Stack>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap" useFlexGap>
          {(data.combos || []).map((o) => {
            const on = combo && combo.join() === o.combo.join();
            return (
              <Paper key={o.combo.join()} variant="outlined" onClick={() => pickCombo(o)}
                sx={{ p: 1.5, minWidth: 200, flex: 1, cursor: o.designs.length ? 'pointer' : 'not-allowed', opacity: o.designs.length ? 1 : 0.45, borderColor: on ? 'primary.main' : undefined, borderWidth: on ? 2 : 1 }}>
                <Typography fontWeight={700}>{o.combo.join(' · ')}</Typography>
                <Typography variant="body2">{o.combo.map((c) => (c.length === 1 ? SCREEN_LABEL[c] : c.split('').map((x) => SCREEN_LABEL[x]).join('+'))).join(' / ')}</Typography>
                <Typography variant="caption" color={o.designs.length ? 'text.secondary' : 'warning.main'}>{o.designs.length ? `디자인: ${o.designs.map((d) => designLabel[d]).join(', ')}` : '디자인 준비 중'}</Typography>
              </Paper>
            );
          })}
        </Stack>
        {mScreens > 0 && (
          <Box mt={2}>
            <Typography variant="body2" fontWeight={600} mb={1}><MovieIcon fontSize="small" sx={{ verticalAlign: 'middle' }} /> M 비디오</Typography>
            {Array.from({ length: mScreens }, (_, k) => (
              <Stack key={k} direction={{ xs: 'column', sm: 'row' }} spacing={1} mb={1} alignItems={{ sm: 'center' }}>
                <TextField select size="small" label={`M${mScreens > 1 ? k + 1 : ''}`} value={videoChoice[k] || 'default'} onChange={(e) => setVideoChoice((x) => ({ ...x, [k]: e.target.value }))} sx={{ minWidth: 320 }}>
                  <MenuItem value="default">매장 기본 비디오 유지</MenuItem>
                  {videos.map((v) => <MenuItem key={v.contentId} value={String(v.contentId)}>{v.photovid ? '🎬 ' : ''}{v.name} · {new Date(v.at).toLocaleDateString()}</MenuItem>)}
                </TextField>
                <Button size="small" href="/Photovid" target="_blank">PhotoVid 로 새로 만들기</Button>
              </Stack>
            ))}
          </Box>
        )}
      </Paper>
      {fitInfo && (
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap mt={1}>
          {fitInfo.screens.flatMap((x) => (x.fit || []).map((f) => (
            <Chip key={`${x.screen}.${f.section}`} size="small" variant="outlined"
              color={f.count > f.max ? 'error' : f.count > f.normal ? 'warning' : 'default'}
              label={`${f.label} ${f.count}/${f.normal}${f.max > f.normal ? ` (최대 ${f.max}${f.count > f.normal ? ` · 글자 ${Math.round(f.scale * 100)}%` : ''})` : ''}`} />
          )))}
          {fitInfo.overflow.map((o) => <Chip key={o.code} size="small" color="error" label={`자리 없음: ${o.name}`} onClick={() => setFull({ code: o.code, o, off: [], enabledNow: true })} />)}
        </Stack>
      )}


      {chosen && (data.promoScreens?.[design] || []).some((c) => combo.includes(c)) && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={1}>
            <Typography fontWeight={700}>오른쪽 화면 (프로모)</Typography>
            <Typography variant="caption" color="text.secondary">화면마다 오른쪽 광고를 고르세요. 카드 가격은 이 매장 가격으로 채워집니다.</Typography>
            {data.canImport && <Button size="small" onClick={() => setUploading(true)}>+ 프로모 올리기</Button>}
          </Stack>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
            {combo.filter((c) => (data.promoScreens?.[design] || []).includes(c)).map((c) => (
              <Stack key={c} spacing={1} alignItems="flex-start">
                <TextField select size="small" label={`${c} 화면`} value={promoSel[c] || ''} onChange={(e) => setPromoSel((x) => ({ ...x, [c]: e.target.value }))} sx={{ minWidth: 240 }}>
                  <MenuItem value="">디자인 기본 (지금 그림)</MenuItem>
                  {promos.map((p) => <MenuItem key={p._id} value={p._id}>{p.name}{p.items?.length ? ` · 매장 가격 ${p.items.length}개 메뉴` : ''}</MenuItem>)}
                </TextField>
                {promoSel[c] && <PromoThumb id={promoSel[c]} />}
              </Stack>
            ))}
          </Stack>
        </Paper>
      )}
      <PromoUploadDialog open={uploading} onClose={() => setUploading(false)} onDone={() => { setUploading(false); api.get('/menu-promos').then((r) => setPromos(r.data.promos)); }} />

      {/* 2. 메뉴·가격 */}
      <Paper variant="outlined" sx={{ p: { xs: 1, md: 2 }, mt: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography fontWeight={700}>2. 메뉴 · 가격 ($)</Typography>
          <Button size="small" onClick={() => setItems((xs) => xs.map((i) => ({ ...i, prices: { ...i.tierPrices } })))}>{data.store.tier} 기본가로</Button>
        </Stack>
        <TableContainer>
          <Table size="small" sx={{ '& td, & th': { px: 0.75 } }}>
            <TableHead><TableRow><TableCell padding="checkbox" /><TableCell>메뉴</TableCell>{S.map((s) => <TableCell key={s} align="center">{L[s]}</TableCell>)}</TableRow></TableHead>
            <TableBody>
              {GROUPS.map((g) => {
                const rows = items.filter(g.test);
                if (!rows.length) return null;
                return [
                  <TableRow key={g.key}><TableCell colSpan={2 + S.length} sx={{ bgcolor: 'action.hover', fontWeight: 700 }}>{g.label}</TableCell></TableRow>,
                  ...rows.map((i) => (
                    <TableRow key={i.code} sx={{ opacity: i.enabled ? 1 : 0.45 }}>
                      <TableCell padding="checkbox"><Checkbox size="small" checked={i.enabled} onChange={(e) => toggleItem(i.code, e.target.checked)} /></TableCell>
                      <TableCell><Typography variant="body2" fontWeight={600}>{i.name}</Typography><Typography variant="caption" color="text.secondary">{i.nameKo}{i.sizeNote ? ` · ${i.sizeNote}` : ''}</Typography></TableCell>
                      {S.map((s) => (
                        <TableCell key={s} align="center">
                          {i.sizes.includes(s) ? (
                            <TextField size="small" disabled={!i.enabled} value={dollars(i.prices[s])} placeholder={dollars(i.tierPrices[s])}
                              onChange={(e) => setPrice(i.code, s, e.target.value)} inputProps={{ inputMode: 'decimal', style: { textAlign: 'center', width: 52, padding: '4px 2px' } }}
                              error={i.enabled && i.prices[s] != null && !(i.prices[s] > 0)} />
                          ) : <Typography variant="caption" color="text.disabled">—</Typography>}
                        </TableCell>
                      ))}
                    </TableRow>
                  )),
                ];
              })}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* 3. 미리보기 · 저장 */}
      <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
        <Typography fontWeight={700} mb={1}>3. 미리보기 · 저장</Typography>
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap mb={1.5}>
          {(combo || []).filter((c) => c !== 'M').map((c) => <Button key={c} size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => doPreview(c)}>{c} 미리보기</Button>)}
        </Stack>
        <TextField size="small" fullWidth label="메모 (점주 미팅 내용 등)" value={note} onChange={(e) => setNote(e.target.value)} sx={{ mb: 1.5 }} />
        {data.canSave
          ? <Button variant="contained" startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />} disabled={saving || !chosen} onClick={save}>저장하고 이미지 만들기</Button>
          : <Alert severity="info">저장은 TLJ 지역 매니저·다인텍만 할 수 있습니다. 이미지는 아래에서 내려받을 수 있습니다.</Alert>}
      </Paper>

      {result && <VersionView storeId={storeId} v={result} />}

      {history.length > 0 && (
        <Paper variant="outlined" sx={{ p: 2, mt: 2 }}>
          <Typography fontWeight={700} mb={1}>저장 기록</Typography>
          {history.map((h) => (
            <Stack key={h.version} direction="row" spacing={1} alignItems="center" py={0.5}>
              <Button size="small" onClick={async () => setResult((await api.get(`/menu-boards/${storeId}/versions/${h.version}`)).data.version)}>v{h.version}</Button>
              <Typography variant="body2">{new Date(h.createdAt).toLocaleString()} · {h.savedBy?.name || ''} · {h.combo.join(' ')} · 변경 {h.changes?.length || 0}</Typography>
              {h.status !== 'ready' && <Chip size="small" color="error" label={h.status} />}
            </Stack>
          ))}
        </Paper>
      )}

      <Dialog open={!!full} onClose={() => setFull(null)} fullWidth maxWidth="xs">
        {full && (
          <DialogContent>
            <Typography variant="h6" fontWeight={700}>자리가 가득 찼습니다</Typography>
            {full.o.section ? (
              <Typography variant="body2" mt={1}>
                <b>{full.o.label}</b> 는 최대 <b>{full.o.max}개</b>까지 들어갑니다 (글자를 조금 줄인 경우 포함).
                <b> {nameOf(full.code)}</b> 를 {full.enabledNow ? '넣으려면' : '추가하려면'} 먼저 이 표에서 뺄 메뉴를 고르세요.
              </Typography>
            ) : (
              <Typography variant="body2" mt={1}>{full.o.label} — 이 디자인에는 {nameOf(full.code)} 자리가 없습니다. 메뉴를 끄거나 다른 디자인을 고르세요.</Typography>
            )}
            {full.o.codes.map((c) => (
              <Stack key={c} direction="row" alignItems="center">
                <Checkbox size="small" checked={full.off.includes(c)} onChange={(e) => setFull((f) => ({ ...f, off: e.target.checked ? [...f.off, c] : f.off.filter((x) => x !== c) }))} />
                <Typography variant="body2">{nameOf(c)} 빼기</Typography>
              </Stack>
            ))}
          </DialogContent>
        )}
        <DialogActions>
          {full?.enabledNow && <Button onClick={() => { setItem(full.code, { enabled: false }); setFull(null); }}>{full && nameOf(full.code)} 끄기</Button>}
          <Button onClick={() => setFull(null)}>취소</Button>
          <Button variant="contained" disabled={!full?.o.section || !full?.off.length} onClick={confirmSwap}>빼고 넣기</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!preview} onClose={() => setPreview(null)} maxWidth="lg" fullWidth>
        <DialogContent sx={{ p: 1 }}>
          {preview?.warnings?.map((w) => <Alert key={w} severity="warning" sx={{ mb: 0.5 }}>{w}</Alert>)}
          {preview && <img src={preview.url} alt={preview.screen} style={{ width: '100%' }} />}
        </DialogContent>
        <DialogActions><Button onClick={() => setPreview(null)}>닫기</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

function VersionView({ storeId, v }) {
  const warnings = v.screens.flatMap((s) => s.warnings || []);
  return (
    <Paper variant="outlined" sx={{ p: 2, mt: 2, borderColor: 'success.main' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1} flexWrap="wrap" useFlexGap spacing={1}>
        <Typography fontWeight={700}>v{v.version} 이미지 {v.emailSentAt ? '· 다인텍에 메일 보냄' : ''}</Typography>
        <Button variant="contained" color="success" startIcon={<DownloadIcon />} href={`${baseApi}/menu-boards/${storeId}/versions/${v.version}/zip`}>전체 다운로드 (ZIP)</Button>
      </Stack>
      {v.status === 'failed' && <Alert severity="error">생성 실패: {v.error}</Alert>}
      {warnings.map((w) => <Alert key={w} severity="warning" sx={{ mb: 0.5 }}>{w}</Alert>)}
      <Box display="grid" gridTemplateColumns={{ xs: '1fr', md: '1fr 1fr' }} gap={1.5} mt={1}>
        {v.screens.map((s, k) => (
          <Box key={k}>
            <Typography variant="body2" fontWeight={600}>{s.code}{s.code === 'M' ? ` · ${s.video?.name || '매장 기본 비디오'}` : ''}</Typography>
            {s.file ? (
              <>
                <a href={fileUrl(storeId, v.version, s.file)} target="_blank" rel="noreferrer"><img src={fileUrl(storeId, v.version, s.file)} alt={s.code} style={{ width: '100%', border: '1px solid #ddd' }} /></a>
                <Button size="small" startIcon={<DownloadIcon />} href={fileUrl(storeId, v.version, s.file, true)}>다운로드</Button>
              </>
            ) : <Paper variant="outlined" sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>{s.code === 'M' ? '비디오 화면' : '이미지 없음'}</Paper>}
          </Box>
        ))}
      </Box>
      {v.changes?.length > 0 && (<><Divider sx={{ my: 1.5 }} /><Typography variant="body2" fontWeight={600}>바뀐 내용 {v.changes.length}</Typography>
        {v.changes.slice(0, 30).map((c, k) => <Typography key={k} variant="caption" display="block">{c.name} {c.field === 'enabled' ? (c.after ? '켬' : '끔') : `${c.field}: ${dollars(c.before) || '—'} → ${dollars(c.after) || '—'}`}</Typography>)}</>)}
      <Typography variant="caption" color="text.secondary" display="block" mt={1}>TV 반영은 다인텍 디자이너가 확인 후 진행합니다.</Typography>
    </Paper>
  );
}

export default function MenuBoardEditor() {
  const { storeId } = useParams();
  return storeId ? <Editor storeId={storeId} /> : <StorePicker />;
}
